import React, { useEffect, useMemo } from 'react';
import styled from 'styled-components';
import { useIntl } from 'react-intl';
import { SdiCheck, SdiHeart, SdiLicense, SdiProfile, SdiShip } from '@sdir/sds';
import { isToggleOn } from '@sdir/blueprint.aps/lib/helpers/permissionUtilities';
import { routes } from '../../../constants/routes';
import { useNavigateWithLang } from '../../../services/hooks/useNavigateWithLang';
import { NavCard } from '../../Molecules/NavCard/NavCard';
import {
  useCurrentPerson,
  useCurrentRegisteredPerson,
  useFeatureToggles,
} from '../../../services/hooks';
import { isValidNorwegianPnr } from '../../../helpers/userHelpers';
import { ToggleDisableEducatia, ToggleDisableSid } from '../../../constants/permissions';
import { MyEducatia } from '../../Organisms/MyEducatia/MyEducatia';

type NewHomePageProps = {
  onChangeLanguage: (l: string, skipRefresh?: boolean) => void;
};

export const NewHomePage: React.FC<NewHomePageProps> = ({ onChangeLanguage }) => {
  const intl = useIntl();
  const navigate = useNavigateWithLang();
  const {
    person: { result: contactInfo },
  } = useCurrentPerson();
  const {
    person: { result: registeredPerson },
  } = useCurrentRegisteredPerson();
  const featureToggle = useFeatureToggles();
  const enabledSid = !isToggleOn(
    featureToggle?.result,
    ToggleDisableSid,
    contactInfo?.personalIdentificationNumber || ''
  );
  const enabledEducatia = !isToggleOn(
    featureToggle?.result,
    ToggleDisableEducatia,
    contactInfo?.personalIdentificationNumber || ''
  );

  const hasValidPnr = useMemo(() => {
    return isValidNorwegianPnr(
      contactInfo?.personalIdentificationNumber || registeredPerson?.personNumber || ''
    );
  }, [contactInfo, registeredPerson]);

  useEffect(() => {
    const { search } = window.location;
    if (search.indexOf('transactionId') !== -1 || search.indexOf('responseCode') !== -1) {
      window.history.replaceState({}, document.title, `${window.location.pathname}#/`);
      window.sessionStorage.setItem('payment_window_step', '2');
      const sessionStorageUrl = window.sessionStorage.getItem('application_payment_step_url');
      if (sessionStorageUrl?.indexOf('/en/') !== -1) {
        onChangeLanguage('en', true);
      }
      navigate({
        pathname: sessionStorageUrl as string,
        search,
      });
    }
  }, [navigate, onChangeLanguage]);

  return (
    <Wrapper>
      <Heading>{intl.formatMessage({ id: 'root.page.title' })}</Heading>
      <TileContainer>
        <NavCard
          heading={intl.formatMessage({ id: 'certificateDashboard.page.title' })}
          description={intl.formatMessage({ id: 'certificateDashboard.page.description' })}
          PrimaryIcon={SdiLicense}
          SecondaryIcon={SdiCheck}
          onClick={() => navigate(routes.certificateOverview)}
        />
        <NavCard
          heading={intl.formatMessage({ id: 'competenceDashboard.page.title' })}
          description={intl.formatMessage({ id: 'competenceDashboard.page.description' })}
          PrimaryIcon={SdiShip}
          SecondaryIcon={SdiHeart}
          onClick={() => navigate(routes.competenceDashboard)}
        />
        {hasValidPnr && enabledSid && (
          <NavCard
            heading={intl.formatMessage({ id: 'sidDashboard.page.title' })}
            description={intl.formatMessage({ id: 'sidDashboard.page.description' })}
            PrimaryIcon={SdiLicense}
            SecondaryIcon={SdiProfile}
            onClick={() => navigate(routes.sidDashboard)}
          />
        )}
      </TileContainer>
      {enabledEducatia && <MyEducatia hideTitle hideExisting />}
    </Wrapper>
  );
};

const TileContainer = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;

  & > * {
    margin: 0 20px 20px 0;
  }

  @media screen and (max-width: ${({ theme }) => theme.screen.medium}) {
    flex-direction: column;
    > * {
      flex: 1;
      margin: 0 0 20px 0;
    }
    a article {
      width: 100%;
    }
    a article ~ div {
      width: calc(100% - 10px);
    }
  }
`;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  margin: 3rem auto;
`;

const Heading = styled.h1`
  font-size: 32px;
  font-weight: 600;
  margin-bottom: 40px;

  @media screen and (max-width: ${({ theme }) => theme.screen.medium}) {
    font-size: 28px;
  }
`;
